/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Text,
    useMultiStyleConfig,
    IconButton,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import CurrentPrice from './current-price'
import ListPrice from './list-price'
import {FormattedMessage} from 'react-intl'
import {Tooltip} from '@chakra-ui/react'
import {InfoCircle} from '../../components/icons'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

/**
 * @param priceData - price info extracted from a product
 * // If a product is a set,
 *      on PLP, Show From X where X is the lowest of its children
 *      on PDP, Show From X where X is the lowest of its children and
 *          the set children will have it own price as From X (cross) Y
 * // if a product is a master
 *      on PLP and PDP, show From X (cross) Y , the X and Y are
 *          current and list price of variant that has the lowest price (including promotionalPrice)
 * // if a standard/bundle
 *      show exact price on PLP and PDP as X (cross) Y
 * @param currency - currency
 * @param quantity - quantity to take into the account for price display
 * @param currentPriceProps - extra props to be passing to CurrentPrice component
 * @param listPriceProps - extra props to be passing to ListPrice component
 * @param labelForA11y - label to be used for a11y
 */
const DisplayPrice = ({
    labelForA11y,
    priceData,
    currency,
    quantity = 1,
    currentPriceProps = {},
    listPriceProps = {},
    priceSetProps = {},
    containerProps = {},
    isDisplayPricePerUnit = false,
    showQuantity = false,
    isPDP = false
}) => {
    const styles = useMultiStyleConfig('DisplayPrice')
    const {
        listPrice,
        currentPrice,
        isASet,
        isMaster,
        isOnSale,
        isRange,
        pricePerUnit,
        newArrivalDate,
        hasPromotion,
        hasOnlyApproachingPromotion,
        isFixedPriceModel
    } = priceData
    const {locale} = useMultiSite()

    const isUSLocale = /[-_]US$/i.test(locale?.id || '')
    const formattedArrivalDate = React.useMemo(() => {
        if (!newArrivalDate) return null

        const parsedDate = new Date(newArrivalDate)
        if (Number.isNaN(parsedDate.getTime())) return null

        const month = new Intl.DateTimeFormat('en-US', {
            month: 'short',
            timeZone: 'UTC'
        }).format(parsedDate)

        return `${month}, ${parsedDate.getUTCFullYear()}`
    }, [newArrivalDate])

    const shouldShowPriceAsOfTooltip = isUSLocale && !!formattedArrivalDate
    const tooltipLabel = shouldShowPriceAsOfTooltip ? `Price as of ${formattedArrivalDate}` : ''
    const {isOpen, onOpen, onToggle, onClose} = useDisclosure()
    const pointerTypeRef = useRef(null)
    const renderCurrentPrice = (isRange) => {
        // Show red if on sale OR has a real promotion (not just approaching discount)
        const shouldShowRed =
            !isFixedPriceModel &&
            listPrice &&
            (isOnSale || (hasPromotion && !hasOnlyApproachingPromotion))
        const priceProps = shouldShowRed
            ? isPDP
                ? {...currentPriceProps}
                : {...currentPriceProps, ...styles.currentPricePromo}
            : currentPriceProps

        return (
            <>
                {showQuantity && (
                    <Text {...styles.quantityText}>
                        <FormattedMessage id="checkout_bag.item_quantity" defaultMessage="{quantity} x " values={{quantity}} />
                    </Text>
                )}
                <CurrentPrice
                    labelForA11y={labelForA11y}
                    price={isDisplayPricePerUnit ? pricePerUnit : currentPrice}
                    currency={currency}
                    isRange={isRange}
                    isUnitPrice={isDisplayPricePerUnit}
                    {...priceProps}
                />
            </>
        )
    }

    const renderListPrice = (isRange) =>
        !isFixedPriceModel &&
        listPrice &&
        listPrice !== currentPrice && (
            <ListPrice
                labelForA11y={labelForA11y}
                currency={currency}
                price={
                    isDisplayPricePerUnit && !(isOnSale || hasPromotion) ? pricePerUnit : listPrice
                }
                isRange={isRange}
                {...listPriceProps}
            />
        )

    const renderPriceSet = (isRange) => (
        <Flex {...styles.priceSet} {...priceSetProps}>
            {renderCurrentPrice(isRange)}
            {!isFixedPriceModel && (isOnSale || hasPromotion) && renderListPrice(isRange)}
            {!isFixedPriceModel && (isOnSale || hasPromotion) && shouldShowPriceAsOfTooltip && (
                <Tooltip isOpen={isOpen} hasArrow={false} placement="top" variant="info" label={tooltipLabel}>
                    <IconButton
                        variant="unstyled"
                        display={'flex'}
                        minW={'auto'}
                        h={'20px'}
                        width={'20px'}
                        onPointerEnter={(event) => {
                            if (event.pointerType === 'mouse') {
                                onOpen()
                            }
                        }}
                        onPointerLeave={(event) => {
                            if (event.pointerType === 'mouse') {
                                onClose()
                            }
                        }}
                        onFocus={onOpen}
                        onBlur={onClose}
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            onToggle()
                        }}
                        aria-label="Aria label button"
                        icon={<InfoCircle width={'20px'} height={'20px'} />}
                    />
                </Tooltip>
            )}
        </Flex>
    )

    if (isASet) {
        return renderCurrentPrice(true)
    }

    if (isMaster) {
        return renderPriceSet(isRange)
    }

    return (
        <Box {...styles.container} {...containerProps}>
            {renderPriceSet(false)}
        </Box>
    )
}

DisplayPrice.propTypes = {
    priceData: PropTypes.shape({
        currentPrice: PropTypes.number.isRequired,
        isOnSale: PropTypes.bool.isRequired,
        listPrice: PropTypes.number,
        pricePerUnit: PropTypes.number,
        isASet: PropTypes.bool,
        isMaster: PropTypes.bool,
        isRange: PropTypes.bool,
        maxPrice: PropTypes.number,
        tieredPrice: PropTypes.number,
        hasOnlyApproachingPromotion: PropTypes.bool,
        hasPromotion: PropTypes.bool,
        newArrivalDate: PropTypes.string,
        isFixedPriceModel: PropTypes.bool
    }),
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    currentPriceProps: PropTypes.object,
    listPriceProps: PropTypes.object,
    priceSetProps: PropTypes.object,
    containerProps: PropTypes.object,
    quantity: PropTypes.number,
    isDisplayPricePerUnit: PropTypes.bool,
    showQuantity: PropTypes.bool,
    isPDP: PropTypes.bool
}

export default DisplayPrice
